import { Box, Flex, Input, Select, Switch, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { HelperTooltip } from 'components/Tooltip';
import { SCENARIO_ADJUSTMENT_MODE } from './calc/applyRoasScenarioAdjustment';

const SCENARIOS_TOOLTIP =
  'Bull and Bear run alongside the Base case when you press Calculate. Each shifts the whole cumulative ROAS vector actually used (Hill spend vectors when fit, otherwise the generic vector) so every period is affected. Bull raises returns, Bear lowers them. Relative % scales every period proportionally (50 → ×1.5). Absolute adds / subtracts ROAS points to each period’s return (e.g. base [5,14,28,46,62] with bull +5 → [10,24,43,66,87]).';

function scenarioHelperText(config, direction) {
  const value = Math.max(0, Number(config?.value) || 0);
  if (!config?.enabled || value <= 0) {
    return 'Off';
  }
  const up = direction > 0;
  if (config.mode === SCENARIO_ADJUSTMENT_MODE.ABSOLUTE) {
    return `${up ? '+' : '−'}${value} ROAS points to each period’s return (whole curve ${
      up ? 'steeper' : 'flatter'
    })`;
  }
  const factor = up ? 1 + value / 100 : Math.max(0, 1 - value / 100);
  return `×${factor.toFixed(2)} on every period (${up ? 'up' : 'down'})`;
}

function ScenarioRow({ label, color, direction, config, onChange, compact = false }) {
  const current = config ?? {
    enabled: false,
    mode: SCENARIO_ADJUSTMENT_MODE.PERCENT,
    value: 0,
  };
  return (
    <Flex align="center" gap={compact ? 2 : 3} flexWrap="wrap" py={compact ? 0 : 2}>
      <Switch
        size={compact ? 'sm' : 'md'}
        colorScheme="purple"
        isChecked={Boolean(current.enabled)}
        onChange={(e) => onChange({ ...current, enabled: e.target.checked })}
      />
      <Text w={compact ? '36px' : '52px'} fontSize={compact ? 'xs' : 'md'} fontWeight="semibold" color={color}>
        {label}
      </Text>
      <Select
        size="sm"
        w={compact ? '108px' : '150px'}
        value={current.mode}
        isDisabled={!current.enabled}
        onChange={(e) => onChange({ ...current, mode: e.target.value })}
      >
        <option value={SCENARIO_ADJUSTMENT_MODE.PERCENT}>Relative %</option>
        <option value={SCENARIO_ADJUSTMENT_MODE.ABSOLUTE}>
          Absolute (points)
        </option>
      </Select>
      <Input
        type="number"
        size="sm"
        w={compact ? '64px' : '110px'}
        min={0}
        textAlign="right"
        isDisabled={!current.enabled}
        value={current.value ?? 0}
        onChange={(e) =>
          onChange({ ...current, value: Math.max(0, Number(e.target.value) || 0) })
        }
      />
      {!compact ? (
        <Text fontSize="xs" color="gray.500">
          {scenarioHelperText(current, direction)}
        </Text>
      ) : null}
    </Flex>
  );
}

ScenarioRow.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  direction: PropTypes.number.isRequired,
  config: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

function ScenarioSettingsSection({
  scenarios,
  onChange,
  embedded = false,
  compact = false,
}) {
  const scenarioRows = (
    <>
      <ScenarioRow
        label="Bull"
        color="green.600"
        direction={1}
        config={scenarios?.bull}
        onChange={(next) => onChange('bull', next)}
        compact={compact}
      />
      <ScenarioRow
        label="Bear"
        color="red.600"
        direction={-1}
        config={scenarios?.bear}
        onChange={(next) => onChange('bear', next)}
        compact={compact}
      />
    </>
  );

  const content = (
    <>
      {!embedded ? (
        <Text className="annotation" mb={2}>
          Run Bull and Bear alongside the Base case. The only difference is the
          cumulative ROAS vector, shifted up (Bull) or down (Bear).
        </Text>
      ) : compact ? (
        <Flex align="center" gap={1} mt={2} mb={1}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">
            Bull &amp; Bear
          </Text>
          <HelperTooltip content={SCENARIOS_TOOLTIP} />
        </Flex>
      ) : (
        <Text fontWeight="semibold" mb={2}>
          Bull &amp; Bear
        </Text>
      )}
      {compact ? (
        <Flex gap={4} flexWrap="wrap" align="flex-start">
          {scenarioRows}
        </Flex>
      ) : (
        scenarioRows
      )}
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Box className="card">
      <Flex className="section-header" gap={3} align="center">
        <span className="step-num">6</span>
        <Text fontWeight="semibold">Scenarios — Bull &amp; Bear</Text>
        <HelperTooltip content={SCENARIOS_TOOLTIP} />
      </Flex>
      <Box p={5}>{content}</Box>
    </Box>
  );
}

ScenarioSettingsSection.propTypes = {
  scenarios: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
  compact: PropTypes.bool,
};

export default ScenarioSettingsSection;
